import { CommonModule, isPlatformBrowser } from '@angular/common';
import { DOCUMENT } from '@angular/common';
import {
  Component,
  Input,
  OnInit,
  Renderer2,
  ViewChild,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { fromEvent, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { CartService } from '@digital-platform/rs-cart/services';
import { HeaderResponse, NavMenuColumn } from '@digital-platform/rs-cms/domain';
import {
  RichTextComponent,
  RichTextLinkComponent,
} from '@digital-platform/rs-cms/feature-ptypes';
import { ViewportService } from '@digital-platform/rs-shared/services';
import { IconComponent } from '@digital-platform/rs-shared/ui-brand';
import { DesktopNavListComponent } from '../desktop-nav-list/desktop-nav-list.component';
import { DesktopNavSideLinksComponent } from '../desktop-nav-side-links/desktop-nav-side-links.component';
import { InlineImageNavComponent } from '../inline-image-nav/inline-image-nav.component';
import { MobileNavToggleComponent } from '../mobile-nav-toggle/mobile-nav-toggle.component';
import { NavDropdownComponent } from '../nav-dropdown/nav-dropdown.component';
import { NavHeaderComponent } from '../nav-header/nav-header.component';

@UntilDestroy()
@Component({
  selector: 'digital-platform-nav',
  templateUrl: './nav.component.html',
  styleUrls: ['./nav.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RichTextComponent,
    RichTextLinkComponent,
    InlineImageNavComponent,
    NavDropdownComponent,
    NavHeaderComponent,
    DesktopNavListComponent,
    MobileNavToggleComponent,
    DesktopNavSideLinksComponent,
    IconComponent,
  ],
})
export class NavComponent implements OnInit {
  @ViewChild('navDropdownComponent')
  navDropdownComponent: NavDropdownComponent | null = null;
  @Input() config!: HeaderResponse[0];
  @Input() icons: string[] = [];
  activeMenu: NavMenuColumn[] | null = null;
  mobileNavOpen = false;
  menuKeys: string[] = [];
  mobileHeaderTitle: string | null = null;
  timeoutId: any;
  screenWidth = 0;
  resizeSubscription!: Subscription;

  private platformId = inject(PLATFORM_ID);
  private router = inject(Router);
  private renderer = inject(Renderer2);
  private document = inject(DOCUMENT);
  public cartService = inject(CartService);
  public viewportService = inject(ViewportService);

  showCart = false;

  ngOnInit() {
    this.cartService.currentCartCount
      .pipe(untilDestroyed(this))
      .subscribe((cartCount) => {
        this.showCart = (cartCount && cartCount > 0) || false;
      });

    this.menuKeys = Object.keys(this.config || {}).filter((key) =>
      key.startsWith('menu_')
    );

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.mobileNavOpen = false;
        this.activeMenu = null;
      }
    });

    this.setupWindowResize();
  }

  // TODO: remove tempId once v2 cart page is implemented. feature flag: ecom-v2-cart
  goToCartPage(e: Event) {
    e.stopPropagation();

    this.router.navigate(['/shop/cart', { tempId: Date.now() }]);
  }

  onWindowResize(): void {
    if (this.screenWidth >= 1200) {
      this.closeMobileNav();
    }
  }

  startHover(menu: NavMenuColumn[]): void {
    if (this.timeoutId) clearTimeout(this.timeoutId);
    this.activeMenu = menu;
  }

  resetHover(): void {
    if (this.timeoutId) clearTimeout(this.timeoutId);
  }

  endHover(): void {
    this.timeoutId = setTimeout(() => (this.activeMenu = null), 300);
  }

  toggleMobileNav() {
    if (this.mobileNavOpen) {
      this.closeMobileNav();
    } else {
      this.mobileNavOpen = true;
      this.renderer.setStyle(this.document.body, 'overflow', 'hidden');
      this.renderer.setStyle(this.document.body, 'height', '100%');
    }
  }

  closeMobileNav() {
    this.activeMenu = null;
    this.mobileNavOpen = false;
    this.mobileHeaderTitle = null;
    this.clearBodyClass();
  }

  clearBodyClass() {
    this.renderer.removeStyle(this.document.body, 'overflow');
    this.renderer.removeStyle(this.document.body, 'height');
  }

  private setupWindowResize() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.screenWidth = window.innerWidth;
    this.onWindowResize();

    this.resizeSubscription = fromEvent(window, 'resize')
      .pipe(debounceTime(500))
      .subscribe(() => {
        this.screenWidth = window.innerWidth;
        this.onWindowResize();
      });
  }

  get isDesktop() {
    return this.viewportService.isDesktop;
  }
}
